import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, ArrowLeft, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { ROLE_LABELS } from '../../components/layout/RoleBadge';

/**
 * Friendly destination for users whose role doesn't grant access to the
 * route they tried to visit. Replaces the previous behaviour where role-
 * mismatched users got bounced into an infinite redirect loop (catchall →
 * RoleBasedRedirect → ProtectedRoute → /unauthorized → catchall → …),
 * which rendered as a blank white screen with "Maximum update depth"
 * spam in the console.
 *
 * Critical: this route does NOT wrap in ProtectedRoute, so it's safe to
 * land here for any authenticated user regardless of role.
 */
export default function UnauthorizedPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
      <div
        className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-xl p-8 text-center space-y-6"
        data-testid="unauthorized-page"
      >
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/30">
          <ShieldAlert className="h-8 w-8 text-amber-400" />
        </div>

        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-white">Access not available</h1>
          <p className="text-sm text-slate-400">
            Your account
            {user?.name ? <> (<span className="text-slate-200">{user.name}</span>)</> : null}
            {user?.role ? <> with the <span className="text-slate-200 font-mono text-xs">{ROLE_LABELS[user.role] || user.role}</span> role</> : null}
            {' '}doesn&apos;t have permission to view this page.
          </p>
          <p className="text-xs text-slate-500 pt-2">
            If this is unexpected, ask your administrator to update your role,
            or sign in with a different account.
          </p>
        </div>

        <div className="flex flex-col gap-2 pt-2">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="border-slate-700 hover:bg-slate-800"
            data-testid="unauthorized-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go back
          </Button>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="text-slate-400 hover:text-white hover:bg-slate-800"
            data-testid="unauthorized-logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
